/**
 * Power Platform Admin API client
 * Covers environment lifecycle, DLP policies, managed environments, and capacity/billing
 */

import { requestWithRetry } from "../utils/http.js";
import type { AuthProvider } from "../auth/types.js";
import { RESOURCES } from "../auth/types.js";
import type {
  EnvironmentDetail,
  EnvironmentBackup,
  DlpPolicy,
  DlpConnectorConfig,
  GovernanceConfiguration,
    BillingPolicy,
  ApiListResponse,
} from "./types.js";
import { createChildLogger } from "../utils/logger.js";
import {
  ApiError,
  AuthorizationError,
  RateLimitError,
  wrapApiCall, ValidationError } from "../utils/errors.js";
import {
  POWER_PLATFORM_API_BASE,
  POWER_PLATFORM_API_VERSION,
  POWER_PLATFORM_GOVERNANCE_V1,
  POWER_PLATFORM_GOVERNANCE_V2,
} from "../utils/constants.js";
import {
  validateRowId,
  validateEnvironmentId,
  sanitizeErrorMessage,
} from "../utils/security.js";

const apiLogger = createChildLogger({ module: "admin-api" });

/** Power Platform API (not BAP): tenant-level licensing resources infer the tenant from the token. */
const LICENSING_API_BASE = "https://api.powerplatform.com";
/** The environment backup routes are only registered under this api-version (404 under the current one). */
const BACKUP_API_VERSION = "2018-01-01";
const LICENSING_API_VERSION = "2024-10-01";

export interface PowerPlatformAdminApiConfig {
  authProvider: AuthProvider;
  defaultEnvironmentId: string;
}

/**
 * Client for Power Platform Admin REST API
 */
export class PowerPlatformAdminApi {
  private readonly authProvider: AuthProvider;
  private readonly defaultEnvironmentId: string;

  constructor(config: PowerPlatformAdminApiConfig) {
    this.authProvider = config.authProvider;
    this.defaultEnvironmentId = config.defaultEnvironmentId;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.authProvider.getTokenForResource(RESOURCES.POWER_PLATFORM);
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  private buildUrl(
    path: string,
    queryParams?: Record<string, string | number | undefined>,
    options: { userScope?: boolean; apiVersion?: string } = {}
  ): string {
    // BAP Admin API requires /scopes/admin/ prefix after the resource provider.
    // Insert it automatically if the path starts with /providers/ and doesn't already have it.
    // `userScope` skips that: the same resource read as the signed-in USER, which
    // works for environments the caller can use but does not administer.
    let resolvedPath = path;
    if (!options.userScope && path.startsWith("/providers/") && !path.includes("/scopes/admin/")) {
      // Insert /scopes/admin/ after /providers/Microsoft.BusinessAppPlatform
      resolvedPath = path.replace(
        "/providers/Microsoft.BusinessAppPlatform/",
        "/providers/Microsoft.BusinessAppPlatform/scopes/admin/"
      );
    }
    const base = `${POWER_PLATFORM_API_BASE}${resolvedPath}`;
    const params = new URLSearchParams();
    params.set("api-version", options.apiVersion ?? POWER_PLATFORM_API_VERSION);

    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        if (value !== undefined) {
          params.set(key, String(value));
        }
      }
    }

    return `${base}?${params.toString()}`;
  }

  /**
   * Flow/app PACKAGE endpoints. These are maker-level, not admin-scoped, and
   * carry their own api-version — so they bypass buildUrl (which injects
   * /scopes/admin/) exactly as the governance URLs do.
   */
  private buildPackageUrl(path: string): string {
    return `${POWER_PLATFORM_API_BASE}${path}?api-version=2016-11-01`;
  }

  /** Ask what a package containing this flow would consist of. */
  async listFlowPackageResources(environmentId: string, flowId: string): Promise<unknown> {
    const url = this.buildPackageUrl(
      `/providers/Microsoft.BusinessAppPlatform/environments/${encodeURIComponent(environmentId)}/listPackageResources`,
    );
    const headers = await this.getAuthHeaders();
    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ baseResourceIds: [`/providers/Microsoft.Flow/flows/${flowId}`] }),
      });
      return this.handleResponse<unknown>(response, `List package resources for flow ${flowId}`);
    }, `List package resources for flow ${flowId}`);
  }

  /**
   * Export a flow as a package and return its download link.
   *
   * This is a TWO-STEP maker-level BAP operation, not a single call on the
   * flow: first ask what the package would contain, then export exactly those
   * resources. The previous implementation issued
   * `GET {ProcessSimple}/flows/{id}/exportPackage`, which is not a real
   * endpoint and always returned "No HTTP resource was found".
   */
  async exportFlowPackage(
    environmentId: string,
    flowId: string,
    options: { displayName?: string; description?: string } = {},
  ): Promise<{ packageLink?: { value?: string }; status?: string }> {
    const manifest = (await this.listFlowPackageResources(environmentId, flowId)) as {
      resources?: Record<string, Record<string, unknown>>;
    };

    // Echo the manifest back with a creation type chosen for each resource;
    // without suggestedCreationType the service has nothing to act on.
    const resources: Record<string, Record<string, unknown>> = {};
    for (const [key, resource] of Object.entries(manifest.resources ?? {})) {
      resources[key] = {
        ...resource,
        suggestedCreationType: resource["suggestedCreationType"] ?? "New",
      };
    }

    const url = this.buildPackageUrl(
      `/providers/Microsoft.BusinessAppPlatform/environments/${encodeURIComponent(environmentId)}/exportPackage`,
    );
    const headers = await this.getAuthHeaders();
    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          includedResourceIds: [`/providers/Microsoft.Flow/flows/${flowId}`],
          details: {
            displayName: options.displayName ?? `Flow ${flowId}`,
            ...(options.description !== undefined ? { description: options.description } : {}),
          },
          resources,
        }),
      });
      return this.handleResponse<{ packageLink?: { value?: string }; status?: string }>(
        response,
        `Export flow package ${flowId}`,
      );
    }, `Export flow package ${flowId}`);
  }

  private buildGovernanceUrl(path: string): string {
    return `${POWER_PLATFORM_API_BASE}${path}`;
  }

  private async handleResponse<T>(
    response: Awaited<ReturnType<typeof requestWithRetry>>,
    context: string
  ): Promise<T> {
    const { statusCode, body } = response;

    if (statusCode === 429) {
      try {
        await body.text();
      } catch {
        body.destroy();
      }
      const retryAfter = response.headers["retry-after"];
      let retryMs: number | undefined;
      if (retryAfter) {
        const seconds = parseInt(String(retryAfter), 10);
        if (!Number.isNaN(seconds)) {
          retryMs = seconds * 1000;
        } else {
          const date = new Date(String(retryAfter));
          if (!Number.isNaN(date.getTime())) {
            retryMs = Math.max(0, date.getTime() - Date.now());
          }
        }
      }
      throw new RateLimitError(retryMs);
    }

    // Stream-read body with size limit to prevent OOM
    const MAX_RESPONSE_SIZE = 50 * 1024 * 1024; // 50MB
    const chunks: Buffer[] = [];
    let totalSize = 0;
    for await (const chunk of body as AsyncIterable<Uint8Array>) {
      totalSize += chunk.length;
      if (totalSize > MAX_RESPONSE_SIZE) {
        body.destroy();
        throw new ApiError(`${context}: response too large (max 50MB)`, 413);
      }
      chunks.push(Buffer.from(chunk));
    }
    const responseText = Buffer.concat(chunks).toString("utf-8");

    if (statusCode === 401) {
      throw new AuthorizationError(`${context}: Authentication failed`);
    }

    if (statusCode === 403) {
      const sanitizedBody = sanitizeErrorMessage(responseText);
      apiLogger.error({ statusCode, errorBody: sanitizedBody, context }, "Access denied");
      throw new AuthorizationError(
        `${context}: Requires Power Platform admin role`
      );
    }

    if (statusCode === 404) {
      const sanitizedBody = sanitizeErrorMessage(responseText);
      throw new ApiError(`${context}: Not found - ${sanitizedBody}`, 404);
    }

    if (statusCode >= 400) {
      let errorDetails: unknown;
      try {
        errorDetails = JSON.parse(responseText);
      } catch {
        errorDetails = sanitizeErrorMessage(responseText);
      }
      throw new ApiError(`${context}: API error`, statusCode, errorDetails);
    }

    if (!responseText) {
      // An empty body on any 2xx is a legitimate "success, no content" — the
      // v2 governance DELETE returns an empty 200, not a 204.
      if (statusCode >= 200 && statusCode < 300) {
        return {} as T;
      }
      throw new ApiError(
        `${context}: Server returned empty response (status ${statusCode})`,
        statusCode
      );
    }

    try {
      return JSON.parse(responseText) as T;
    } catch (e) {
      throw new ApiError(
        `${context}: Invalid JSON response (status ${statusCode})`,
        statusCode,
        { parseError: String(e), responsePreview: sanitizeErrorMessage(responseText.slice(0, 200)) }
      );
    }
  }

  // ==========================================================================
  // Environment Lifecycle
  // ==========================================================================

  /**
   * Environment details. Read at ADMIN scope first; if the admin scope says the
   * environment does not exist, read it as the signed-in user instead. The
   * admin scope only lists environments the caller administers — a tenant's
   * default environment is routinely invisible there to someone who is admin
   * of their own Production and Sandbox — and "EnvironmentNotFound" for an
   * environment that plainly exists is misleading.
   */
  async getEnvironment(environmentId?: string): Promise<EnvironmentDetail & { readScope: "admin" | "user" }> {
    const envId = validateEnvironmentId(environmentId ?? this.defaultEnvironmentId);
    apiLogger.debug({ envId }, "Getting environment details");
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const adminUrl = this.buildUrl(`/providers/Microsoft.BusinessAppPlatform/environments/${envId}`);
      const adminResponse = await requestWithRetry(adminUrl, { method: "GET", headers });
      if (adminResponse.statusCode !== 404) {
        const detail = await this.handleResponse<EnvironmentDetail>(adminResponse, `Get environment ${envId}`);
        return { ...detail, readScope: "admin" as const };
      }
      await adminResponse.body.text();
      apiLogger.debug({ envId }, "Environment not visible at admin scope; reading at user scope");
      const userUrl = this.buildUrl(`/providers/Microsoft.BusinessAppPlatform/environments/${envId}`, undefined, {
        userScope: true,
      });
      const userResponse = await requestWithRetry(userUrl, { method: "GET", headers });
      const detail = await this.handleResponse<EnvironmentDetail>(userResponse, `Get environment ${envId}`);
      return { ...detail, readScope: "user" as const };
    }, `Get environment ${envId}`);
  }

  async createEnvironment(
    displayName: string,
    location: string,
    environmentSku: "Production" | "Sandbox" | "Trial",
    description?: string,
    currencyName?: string,
    languageName?: string
  ): Promise<EnvironmentDetail> {
    apiLogger.info({ displayName, location, environmentSku }, "Creating environment");

    const url = this.buildUrl("/providers/Microsoft.BusinessAppPlatform/environments");
    const headers = await this.getAuthHeaders();

    const body: Record<string, unknown> = {
      location,
      properties: {
        displayName,
        description: description ?? "",
        environmentSku,
        linkedEnvironmentMetadata: {
          baseLanguage: 1033,
          currency: { code: currencyName ?? "USD" },
          templates: ["D365_CDSSampleApp"],
        },
      },
    };
    if (languageName) {
      (body.properties as Record<string, unknown>).languageName = languageName;
    }

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      return this.handleResponse<EnvironmentDetail>(response, "Create environment");
    }, "Create environment");
  }

  async deleteEnvironment(environmentId: string): Promise<void> {
    const safeEnvId = validateEnvironmentId(environmentId);
    apiLogger.info({ environmentId: safeEnvId }, "Deleting environment");

    const url = this.buildUrl(
      `/providers/Microsoft.BusinessAppPlatform/environments/${safeEnvId}`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "DELETE", headers });
      await this.handleResponse<unknown>(response, `Delete environment ${safeEnvId}`);
    }, `Delete environment ${safeEnvId}`);
  }

  async backupEnvironment(
    environmentId: string,
    label: string,
    notes?: string
  ): Promise<EnvironmentBackup> {
    const safeEnvId = validateEnvironmentId(environmentId);
    apiLogger.info({ environmentId: safeEnvId, label }, "Backing up environment");

    const url = this.buildUrl(
      `/providers/Microsoft.BusinessAppPlatform/environments/${safeEnvId}/backups`,
      undefined,
      { apiVersion: BACKUP_API_VERSION, userScope: true }
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ label, notes: notes ?? "" }),
      });
      return this.handleResponse<EnvironmentBackup>(response, `Backup environment ${safeEnvId}`);
    }, `Backup environment ${safeEnvId}`);
  }

  async restoreEnvironment(
    environmentId: string,
    backupId: string,
    targetEnvironmentId?: string
  ): Promise<EnvironmentDetail> {
    const safeEnvId = validateEnvironmentId(environmentId);
    const safeBackupId = validateRowId(backupId);
    const safeTargetEnvId = targetEnvironmentId ? validateEnvironmentId(targetEnvironmentId) : safeEnvId;
    apiLogger.info({ environmentId: safeEnvId, backupId: safeBackupId, targetEnvironmentId: safeTargetEnvId }, "Restoring environment");

    const url = this.buildUrl(
      `/providers/Microsoft.BusinessAppPlatform/environments/${safeEnvId}/restores`,
      undefined,
      { apiVersion: BACKUP_API_VERSION, userScope: true }
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          sourceEnvironmentId: safeEnvId,
          targetEnvironmentId: safeTargetEnvId,
          backupId: safeBackupId,
        }),
      });
      return this.handleResponse<EnvironmentDetail>(
        response,
        `Restore environment ${safeEnvId}`
      );
    }, `Restore environment ${safeEnvId}`);
  }

  async copyEnvironment(
    sourceEnvironmentId: string,
    targetEnvironmentDisplayName: string,
    targetEnvironmentLocation: string,
    copyType: "MinimalCopy" | "FullCopy" = "FullCopy"
  ): Promise<EnvironmentDetail> {
    const safeSourceId = validateEnvironmentId(sourceEnvironmentId);
    apiLogger.info({ sourceEnvironmentId: safeSourceId, copyType }, "Copying environment");

    const url = this.buildUrl(
      `/providers/Microsoft.BusinessAppPlatform/environments/${safeSourceId}/copy`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          targetEnvironmentName: targetEnvironmentDisplayName,
          targetLocation: targetEnvironmentLocation,
          copyType,
        }),
      });
      return this.handleResponse<EnvironmentDetail>(
        response,
        `Copy environment ${safeSourceId}`
      );
    }, `Copy environment ${safeSourceId}`);
  }

  async resetEnvironment(
    environmentId: string,
    friendlyName: string,
    currencyName?: string,
    purpose?: string
  ): Promise<EnvironmentDetail> {
    const safeEnvId = validateEnvironmentId(environmentId);
    apiLogger.info({ environmentId: safeEnvId, friendlyName }, "Resetting environment");

    const url = this.buildUrl(
      `/providers/Microsoft.BusinessAppPlatform/environments/${safeEnvId}/resets`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          friendlyName,
          currency: { code: currencyName ?? "USD" },
          purpose: purpose ?? "",
          baseLanguage: 1033,
          templates: ["D365_CDSSampleApp"],
        }),
      });
      return this.handleResponse<EnvironmentDetail>(
        response,
        `Reset environment ${safeEnvId}`
      );
    }, `Reset environment ${safeEnvId}`);
  }

  async listEnvironmentBackups(environmentId: string): Promise<EnvironmentBackup[]> {
    const safeEnvId = validateEnvironmentId(environmentId);
    apiLogger.debug({ environmentId: safeEnvId }, "Listing environment backups");

    const url = this.buildUrl(
      `/providers/Microsoft.BusinessAppPlatform/environments/${safeEnvId}/backups`,
      undefined,
      { apiVersion: BACKUP_API_VERSION, userScope: true }
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<EnvironmentBackup>>(
        response,
        `List backups ${safeEnvId}`
      );
      if (!data?.value || !Array.isArray(data.value)) {
        apiLogger.warn({ context: `List backups ${safeEnvId}`, responseKeys: data ? Object.keys(data) : "null" }, "Unexpected response shape");
        return [];
      }
      return data.value;
    }, `List backups ${safeEnvId}`);
  }

  // ==========================================================================
  // DLP Policies
  // ==========================================================================

  // DLP policies moved from the retired classic `apiPolicies` endpoint to the
  // PowerPlatform.Governance provider (issue #16). The classic endpoint's shape
  // never matched the formatters; the v2 `policyDefinition` shape does.
  //
  // v2 classification names differ from the tool's schema — map on the way in.
  private static readonly CLASSIFICATION_TO_V2: Record<string, string> = {
    Business: "Confidential",
    NonBusiness: "General",
    Blocked: "Blocked",
  };

  private toV2ConnectorGroups(
    groups: Array<{ classification: string; connectors: Array<{ id: string; name?: string; type?: string }> }>
  ): Array<Record<string, unknown>> {
    return groups.map((g) => ({
      // Already-v2 names pass through unchanged.
      classification: PowerPlatformAdminApi.CLASSIFICATION_TO_V2[g.classification] ?? g.classification,
      connectors: g.connectors.map((c) => ({
        id: c.id,
        // v2 rejects a connector reference missing name or type.
        name: c.name ?? c.id.split("/").pop() ?? c.id,
        type: c.type ?? "Microsoft.PowerApps/apis",
      })),
    }));
  }

  private toV2Environments(
    environments?: Array<{ name: string }>
  ): Array<Record<string, unknown>> {
    return (environments ?? []).map((e) => ({
      id: `/providers/admin/environments/${e.name}`,
      name: e.name,
      type: "Microsoft.BusinessAppPlatform/scopes/environments",
    }));
  }

  /** Unwrap a v2 policy item (list or single) into the DlpPolicy shape the formatters expect. */
  private toDlpPolicy(raw: unknown): DlpPolicy {
    const item = (raw ?? {}) as Record<string, unknown>;
    const d = (item.policyDefinition ?? item) as Record<string, unknown>;
    const name = (d.name as string) ?? (item.name as string) ?? "";
    return {
      name,
      id: name,
      type: "PowerPlatform.Governance/policies",
      properties: d as unknown as DlpPolicy["properties"],
    };
  }

  async listDlpPolicies(): Promise<DlpPolicy[]> {
    apiLogger.debug("Listing DLP policies");

    const url = this.buildGovernanceUrl(`${POWER_PLATFORM_GOVERNANCE_V2}/policies`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<{ value?: unknown[] }>(
        response,
        "List DLP policies"
      );
      if (!data?.value || !Array.isArray(data.value)) {
        apiLogger.warn({ context: "List DLP policies", responseKeys: data ? Object.keys(data) : "null" }, "Unexpected response shape");
        return [];
      }
      return data.value.map((raw) => this.toDlpPolicy(raw));
    }, "List DLP policies");
  }

  async getDlpPolicy(policyId: string): Promise<DlpPolicy> {
    const safePolicyId = validateRowId(policyId);
    apiLogger.debug({ policyId: safePolicyId }, "Getting DLP policy");

    const url = this.buildGovernanceUrl(`${POWER_PLATFORM_GOVERNANCE_V2}/policies/${safePolicyId}`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<{ policyDefinition?: unknown }>(
        response,
        `Get DLP policy ${safePolicyId}`
      );
      return this.toDlpPolicy(data);
    }, `Get DLP policy ${safePolicyId}`);
  }

  async createDlpPolicy(
    displayName: string,
    environmentType: "AllEnvironments" | "ExceptEnvironments" | "OnlyEnvironments",
    connectorGroups: Array<{
      classification: "Business" | "NonBusiness" | "Blocked";
      connectors: Array<{ id: string; type?: string }>;
    }>,
    environments?: Array<{ name: string }>
  ): Promise<DlpPolicy> {
    apiLogger.info({ displayName, environmentType }, "Creating DLP policy");

    const url = this.buildGovernanceUrl(`${POWER_PLATFORM_GOVERNANCE_V2}/policies`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          policyDefinition: {
            displayName,
            // New connectors land in the "General" (Non-Business) group by default.
            defaultConnectorsClassification: "General",
            environmentType,
            environments: this.toV2Environments(environments),
            connectorGroups: this.toV2ConnectorGroups(connectorGroups),
          },
        }),
      });
      const data = await this.handleResponse<{ policyDefinition?: unknown }>(
        response,
        "Create DLP policy"
      );
      return this.toDlpPolicy(data);
    }, "Create DLP policy");
  }

  async updateDlpPolicy(
    policyId: string,
    updates: {
      displayName?: string;
      environmentType?: string;
      environments?: Array<{ name: string }>;
      connectorGroups?: Array<{
        classification: string;
        connectors: Array<{ id: string; type?: string }>;
      }>;
    }
  ): Promise<DlpPolicy> {
    const safePolicyId = validateRowId(policyId);
    apiLogger.info({ policyId: safePolicyId }, "Updating DLP policy");

    const url = this.buildGovernanceUrl(`${POWER_PLATFORM_GOVERNANCE_V2}/policies/${safePolicyId}`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      // v2 PATCH rejects a partial body — start from the current full definition
      // and overlay only the changed fields.
      const current = await this.getDlpPolicy(safePolicyId);
      const definition: Record<string, unknown> = {
        ...(current.properties as unknown as Record<string, unknown>),
      };
      if (updates.displayName !== undefined) definition.displayName = updates.displayName;
      if (updates.environmentType !== undefined) definition.environmentType = updates.environmentType;
      if (updates.environments !== undefined) definition.environments = this.toV2Environments(updates.environments);
      if (updates.connectorGroups !== undefined) definition.connectorGroups = this.toV2ConnectorGroups(updates.connectorGroups);

      const response = await requestWithRetry(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ policyDefinition: definition }),
      });
      const data = await this.handleResponse<{ policyDefinition?: unknown }>(
        response,
        `Update DLP policy ${safePolicyId}`
      );
      return this.toDlpPolicy(data);
    }, `Update DLP policy ${safePolicyId}`);
  }

  async deleteDlpPolicy(policyId: string): Promise<void> {
    const safePolicyId = validateRowId(policyId);
    apiLogger.info({ policyId: safePolicyId }, "Deleting DLP policy");

    const url = this.buildGovernanceUrl(`${POWER_PLATFORM_GOVERNANCE_V2}/policies/${safePolicyId}`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      // v2 DELETE returns an empty 200 body — handleResponse tolerates that.
      const response = await requestWithRetry(url, { method: "DELETE", headers });
      await this.handleResponse<unknown>(response, `Delete DLP policy ${safePolicyId}`);
    }, `Delete DLP policy ${safePolicyId}`);
  }

  async getDlpConnectorConfigs(policyId: string): Promise<DlpConnectorConfig[]> {
    const safePolicyId = validateRowId(policyId);
    apiLogger.debug({ policyId: safePolicyId }, "Getting DLP connector configs");

    const url = this.buildGovernanceUrl(
      `${POWER_PLATFORM_GOVERNANCE_V1}/policies/${safePolicyId}/policyConnectorConfigurations`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      let data: ApiListResponse<DlpConnectorConfig>;
      try {
        const response = await requestWithRetry(url, { method: "GET", headers });
        data = await this.handleResponse<ApiListResponse<DlpConnectorConfig>>(
          response,
          `Get DLP connector configs ${safePolicyId}`
        );
      } catch (err) {
        // The route 404s when a policy has no endpoint-filtering configs —
        // that's "none", not an error.
        if (err instanceof ApiError && err.statusCode === 404) {
          return [];
        }
        throw err;
      }
      if (!data?.value || !Array.isArray(data.value)) {
        apiLogger.warn({ context: `Get DLP connector configs ${safePolicyId}`, responseKeys: data ? Object.keys(data) : "null" }, "Unexpected response shape");
        return [];
      }
      return data.value;
    }, `Get DLP connector configs ${safePolicyId}`);
  }

  // ==========================================================================
  // Managed Environments
  // ==========================================================================

  async getManagedEnvironmentSettings(environmentId?: string): Promise<GovernanceConfiguration> {
    const envId = validateEnvironmentId(environmentId ?? this.defaultEnvironmentId);
    apiLogger.debug({ envId }, "Getting managed environment settings");

    // `governanceConfiguration` is written through its own resource but READ
    // as a property of the admin environment record; a GET on the resource
    // path answers 404.
    const url = this.buildUrl(`/providers/Microsoft.BusinessAppPlatform/environments/${envId}`, {
      $expand: "properties.governanceConfiguration",
    });
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const detail = await this.handleResponse<{ properties?: { governanceConfiguration?: GovernanceConfiguration } }>(
        response,
        `Get governance config ${envId}`
      );
      // An environment that was never made Managed has no configuration at all;
      // the platform treats that as Basic protection.
      return detail.properties?.governanceConfiguration ?? { protectionLevel: "Basic" };
    }, `Get governance config ${envId}`);
  }

  async enableManagedEnvironment(
    environmentId: string,
    protectionLevel: "Basic" | "Standard" | "Premium" = "Standard"
  ): Promise<GovernanceConfiguration> {
    const safeEnvId = validateEnvironmentId(environmentId);
    apiLogger.info({ environmentId: safeEnvId, protectionLevel }, "Enabling managed environment");

    const url = this.buildUrl(
      `/providers/Microsoft.BusinessAppPlatform/environments/${safeEnvId}/governanceConfiguration`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ protectionLevel }),
      });
      return this.handleResponse<GovernanceConfiguration>(
        response,
        `Enable managed env ${safeEnvId}`
      );
    }, `Enable managed env ${safeEnvId}`);
  }

  async disableManagedEnvironment(environmentId: string): Promise<void> {
    const safeEnvId = validateEnvironmentId(environmentId);
    apiLogger.info({ environmentId: safeEnvId }, "Disabling managed environment");

    const url = this.buildUrl(
      `/providers/Microsoft.BusinessAppPlatform/environments/${safeEnvId}/governanceConfiguration`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "DELETE", headers });
      await this.handleResponse<unknown>(response, `Disable managed env ${safeEnvId}`);
    }, `Disable managed env ${safeEnvId}`);
  }

  async updateManagedEnvironmentSettings(
    environmentId: string,
    settings: Partial<GovernanceConfiguration>
  ): Promise<GovernanceConfiguration> {
    const safeEnvId = validateEnvironmentId(environmentId);
    apiLogger.info({ environmentId: safeEnvId }, "Updating managed environment settings");

    const url = this.buildUrl(
      `/providers/Microsoft.BusinessAppPlatform/environments/${safeEnvId}/governanceConfiguration`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify(settings),
      });
      return this.handleResponse<GovernanceConfiguration>(
        response,
        `Update governance config ${safeEnvId}`
      );
    }, `Update governance config ${safeEnvId}`);
  }

  // ==========================================================================
  // Capacity & Licensing
  // ==========================================================================

  /**
   * Storage consumption across the tenant, summed from every environment's
   * capacity record — the source Microsoft's own daily-capacity-report tutorial
   * uses (List Environments with `$expand=properties.capacity`). There is no
   * `/tenants/{id}/capacities` resource (it answers 404), and purchased
   * entitlements are not exposed by any public API.
   */
  async getTenantCapacity(): Promise<TenantCapacitySummary> {
    apiLogger.debug("Getting tenant capacity from the environment list");
    const url = this.buildUrl(`/providers/Microsoft.BusinessAppPlatform/environments`, {
      $expand: "properties.capacity",
    });
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<{ name?: string; properties?: { displayName?: string; capacity?: unknown } }>>(
        response,
        "Get tenant capacity"
      );
      const environments = Array.isArray(data?.value) ? data.value : [];
      return summarizeTenantCapacity(environments);
    }, "Get tenant capacity");
  }

  /**
   * The tenant of the signed-in account, so admin tools do not have to ask
   * the caller for a GUID the session already knows.
   */
  async getSignedInTenantId(): Promise<string> {
    const fromAccount = this.authProvider.getCurrentAccount()?.tenantId;
    if (fromAccount && /^[0-9a-f-]{36}$/i.test(fromAccount)) return fromAccount;
    const token = await this.authProvider.getTokenForResource(RESOURCES.POWER_PLATFORM);
    const tid = tenantIdFromJwt(token);
    if (!tid) {
      throw new ValidationError(
        "Could not determine your tenant id from the signed-in session — pass tenantId explicitly."
      );
    }
    return tid;
  }

  /**
   * Per-environment storage consumption.
   *
   * There is no `/environments/{id}/capacities` resource (it answers 404); the
   * admin environment record carries `properties.capacity` when asked for it
   * with `$expand=properties.capacity`, one entry per capacity type with the
   * actual and rated consumption in MB.
   */
  async getEnvironmentCapacity(environmentId?: string): Promise<EnvironmentCapacity[]> {
    const envId = validateEnvironmentId(environmentId ?? this.defaultEnvironmentId);
    apiLogger.debug({ envId }, "Getting environment capacity");

    const url = this.buildUrl(`/providers/Microsoft.BusinessAppPlatform/environments/${envId}`, {
      $expand: "properties.capacity",
    });
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      let response = await requestWithRetry(url, { method: "GET", headers });
      if (response.statusCode === 404) {
        // Not an admin of this environment: the capacity block is admin-only,
        // but read the record as the user so a real environment is not reported
        // as nonexistent. `capacityFromEnvironment` returns [] when absent.
        await response.body.text();
        const userUrl = this.buildUrl(
          `/providers/Microsoft.BusinessAppPlatform/environments/${envId}`,
          { $expand: "properties.capacity" },
          { userScope: true }
        );
        response = await requestWithRetry(userUrl, { method: "GET", headers });
      }
      const detail = await this.handleResponse<{ properties?: { capacity?: unknown } }>(
        response,
        `Get capacity ${envId}`
      );
      return capacityFromEnvironment(detail);
    }, `Get capacity ${envId}`);
  }

  /** Headers for the Power Platform API (api.powerplatform.com), a different audience from BAP. */
  private async getLicensingHeaders(): Promise<Record<string, string>> {
    const token = await this.authProvider.getTokenForResource(RESOURCES.POWER_PLATFORM_API);
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" };
  }

  /**
   * Pay-as-you-go billing policies. These live in the Power Platform API's
   * `licensing` namespace, where the tenant is inferred from the token —
   * not under BAP `/tenants/{id}/billingPolicies`, which does not exist.
   */
  async listBillingPolicies(): Promise<BillingPolicy[]> {
    apiLogger.debug("Listing billing policies");
    const url = `${LICENSING_API_BASE}/licensing/billingPolicies?api-version=${LICENSING_API_VERSION}`;
    const headers = await this.getLicensingHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<BillingPolicy> | BillingPolicy[]>(
        response,
        "List billing policies"
      );
      if (Array.isArray(data)) return data;
      if (!data?.value || !Array.isArray(data.value)) {
        apiLogger.warn({ context: "List billing policies", responseKeys: data ? Object.keys(data) : "null" }, "Unexpected response shape");
        return [];
      }
      return data.value;
    }, "List billing policies");
  }

  async getBillingPolicy(policyId: string): Promise<BillingPolicy> {
    const safePolicyId = validateRowId(policyId);
    apiLogger.debug({ policyId: safePolicyId }, "Getting billing policy");
    const url = `${LICENSING_API_BASE}/licensing/billingPolicies/${safePolicyId}?api-version=${LICENSING_API_VERSION}`;
    const headers = await this.getLicensingHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      return this.handleResponse<BillingPolicy>(response, `Get billing policy ${safePolicyId}`);
    }, `Get billing policy ${safePolicyId}`);
  }
}

/** One row of an environment's `properties.capacity`, as the admin API reports it (MB). */
export interface EnvironmentCapacity {
  capacityType: string;
  actualConsumption: number;
  ratedConsumption: number;
  capacityUnit: string;
  updatedOn?: string;
}

/** Pull the capacity rows out of an admin environment record; tolerant of the field being absent. */
export function capacityFromEnvironment(detail: { properties?: { capacity?: unknown } } | null | undefined): EnvironmentCapacity[] {
  const rows = detail?.properties?.capacity;
  if (!Array.isArray(rows)) return [];
  const out: EnvironmentCapacity[] = [];
  for (const row of rows) {
    if (row === null || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const capacityType = typeof r.capacityType === "string" ? r.capacityType : "Unknown";
    const actual = typeof r.actualConsumption === "number" ? r.actualConsumption : Number(r.actualConsumption ?? 0);
    const rated = typeof r.ratedConsumption === "number" ? r.ratedConsumption : Number(r.ratedConsumption ?? 0);
    out.push({
      capacityType,
      actualConsumption: Number.isFinite(actual) ? actual : 0,
      ratedConsumption: Number.isFinite(rated) ? rated : 0,
      capacityUnit: typeof r.capacityUnit === "string" ? r.capacityUnit : "MB",
      ...(typeof r.updatedOn === "string" ? { updatedOn: r.updatedOn } : {}),
    });
  }
  return out;
}

/** Read the `tid` claim out of an access token without verifying it (we only need to know who we are). */
export function tenantIdFromJwt(token: string): string | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = JSON.parse(Buffer.from(parts[1]!.replace(/-/g, "+").replace(/_/g, "/"), "base64").toString("utf8")) as {
      tid?: unknown;
    };
    return typeof payload.tid === "string" && /^[0-9a-f-]{36}$/i.test(payload.tid) ? payload.tid : null;
  } catch {
    return null;
  }
}

/** Tenant-wide storage consumption derived from every environment's capacity record. */
export interface TenantCapacitySummary {
  byType: EnvironmentCapacity[];
  environments: Array<{ id: string; displayName: string; capacity: EnvironmentCapacity[] }>;
}

export function summarizeTenantCapacity(
  environments: ReadonlyArray<{ name?: string; properties?: { displayName?: string; capacity?: unknown } }>
): TenantCapacitySummary {
  const totals = new Map<string, EnvironmentCapacity>();
  const rows: TenantCapacitySummary["environments"] = [];
  for (const env of environments) {
    const capacity = capacityFromEnvironment(env);
    rows.push({ id: env.name ?? "", displayName: env.properties?.displayName ?? env.name ?? "(unnamed)", capacity });
    for (const c of capacity) {
      const t = totals.get(c.capacityType) ?? {
        capacityType: c.capacityType,
        actualConsumption: 0,
        ratedConsumption: 0,
        capacityUnit: c.capacityUnit,
      };
      t.actualConsumption = Math.round((t.actualConsumption + c.actualConsumption) * 1000) / 1000;
      t.ratedConsumption = Math.round((t.ratedConsumption + c.ratedConsumption) * 1000) / 1000;
      totals.set(c.capacityType, t);
    }
  }
  return { byType: [...totals.values()], environments: rows };
}
